// React Query hooks for the Renewal Board surface — FE-SCRM-008.

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import {
  createPreset,
  deletePreset,
  fetchPresets,
  fetchRenewalBoard,
  fetchRenewalBoardCell,
  fetchRenewalBoardTimeline,
  fetchResearchQueue,
  hidePreset,
  moveOpportunityStage,
  patchOpportunityRenewal,
  patchPreset,
  pinPresetAsDefault,
  unhidePreset,
  type BoardWindow,
  type CellQuery,
  type MoveStageInput,
} from "./api";
import type {
  BoardFilters,
  PresetListResponse,
  RenewalBoardCellResponse,
  RenewalBoardOpportunity,
  RenewalBoardPreset,
  RenewalBoardResearchQueueResponse,
  RenewalBoardResponse,
  RenewalBoardTimelineResponse,
} from "./types";


export const RENEWAL_BOARD_QUERY_KEY = "renewalBoard" as const;
export const RENEWAL_BOARD_PRESETS_QUERY_KEY = [
  RENEWAL_BOARD_QUERY_KEY, "presets",
] as const;


export function useRenewalBoardQuery(
  window: BoardWindow,
  filters: BoardFilters,
): UseQueryResult<RenewalBoardResponse> {
  return useQuery({
    queryKey: [RENEWAL_BOARD_QUERY_KEY, "grid", window, filters],
    queryFn: ({ signal }) => fetchRenewalBoard(window, filters, { signal }),
  });
}


export function useRenewalBoardCellQuery(
  q: CellQuery | null,
): UseQueryResult<RenewalBoardCellResponse> {
  return useQuery({
    queryKey: [RENEWAL_BOARD_QUERY_KEY, "cell", q],
    queryFn: ({ signal }) => fetchRenewalBoardCell(q!, { signal }),
    enabled: q !== null,
  });
}


export function useRenewalBoardTimelineQuery(
  filters: BoardFilters,
  enabled: boolean,
): UseQueryResult<RenewalBoardTimelineResponse> {
  return useQuery({
    queryKey: [RENEWAL_BOARD_QUERY_KEY, "timeline", filters],
    queryFn: ({ signal }) =>
      fetchRenewalBoardTimeline(filters, { signal }),
    enabled,
  });
}


export function useResearchQueueQuery(
  filters: BoardFilters,
  enabled: boolean,
): UseQueryResult<RenewalBoardResearchQueueResponse> {
  return useQuery({
    queryKey: [RENEWAL_BOARD_QUERY_KEY, "research-queue", filters],
    queryFn: ({ signal }) => fetchResearchQueue(filters, { signal }),
    enabled,
  });
}


export function usePresetsQuery(): UseQueryResult<PresetListResponse> {
  return useQuery({
    queryKey: RENEWAL_BOARD_PRESETS_QUERY_KEY,
    queryFn: ({ signal }) => fetchPresets({ signal }),
  });
}


// ── Mutations ───────────────────────────────────────────────────


export function useCreatePresetMutation(): UseMutationResult<
  RenewalBoardPreset,
  Error,
  { name: string; filter_json: Record<string, unknown> }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createPreset,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: RENEWAL_BOARD_PRESETS_QUERY_KEY });
    },
  });
}


export function usePatchPresetMutation(): UseMutationResult<
  RenewalBoardPreset,
  Error,
  {
    preset_id: string;
    patch: Partial<{
      name: string;
      filter_json: Record<string, unknown>;
      display_order: number;
      is_hidden: boolean;
    }>;
  }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ preset_id, patch }) => patchPreset(preset_id, patch),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: RENEWAL_BOARD_PRESETS_QUERY_KEY });
    },
  });
}


export function useDeletePresetMutation(): UseMutationResult<
  unknown,
  Error,
  string
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: deletePreset,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: RENEWAL_BOARD_PRESETS_QUERY_KEY });
    },
  });
}


export function usePinPresetMutation(): UseMutationResult<
  RenewalBoardPreset,
  Error,
  string
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: pinPresetAsDefault,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: RENEWAL_BOARD_PRESETS_QUERY_KEY });
    },
  });
}


export function useHidePresetMutation(): UseMutationResult<
  RenewalBoardPreset,
  Error,
  { preset_id: string; hidden: boolean }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ preset_id, hidden }) =>
      hidden ? hidePreset(preset_id) : unhidePreset(preset_id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: RENEWAL_BOARD_PRESETS_QUERY_KEY });
    },
  });
}


// ── Opportunity moves (drag persistence) ────────────────────────


export function useMoveOpportunityStageMutation(): UseMutationResult<
  RenewalBoardOpportunity,
  Error,
  { opportunity_id: string } & MoveStageInput
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ opportunity_id, ...input }) =>
      moveOpportunityStage(opportunity_id, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [RENEWAL_BOARD_QUERY_KEY] });
    },
  });
}


export function useChangeRenewalMonthMutation(): UseMutationResult<
  RenewalBoardOpportunity,
  Error,
  { opportunity_id: string; renewal_month: number; renewal_year: number }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ opportunity_id, ...input }) =>
      patchOpportunityRenewal(opportunity_id, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [RENEWAL_BOARD_QUERY_KEY] });
    },
  });
}
